import { api, ApiError } from "../lib/api.js";
import {
  LFG_MODES,
  LFG_MODE_LABELS,
  LFG_NOTE_MAX,
  LINK_LABELS,
  LINK_PLATFORMS,
  REGIONS,
  REGION_LABELS,
  REPORT_REASONS,
  REPORT_REASON_LABELS,
  expiresIn,
  handleProblem,
  handleProblemMessage,
  type ChallengeState,
  type LfgMode,
  type LinkPlatform,
  type Region,
  type ReportReason,
} from "@rp/shared";

/**
 * Profile, the players board, collaborations and challenges.
 *
 * One file because they are one feature, and because the thing that has to stay
 * true across all of them is a single rule: nothing a streamer types here is
 * visible to anybody until they publish it, and the screen has to say so loudly
 * enough that nobody is surprised.
 *
 * There is no inbox, by design. Every answer to a post goes through a link the
 * poster published, which the board shows directly. See
 * `migrations/038_creator_social.sql` for why.
 *
 * Handle rules are validated here as well as on the server, using the same
 * module: a form that accepts a handle the server then refuses is the worst
 * version of this page.
 */

const esc = (v: string) =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

interface Profile {
  handle: string | null;
  display_name: string | null;
  bio: string | null;
  links: Partial<Record<LinkPlatform, string>>;
  region: Region | null;
  languages: string[];
  looking_for: string;
  published_at: string | null;
  main_games: { game_name: string; clips: number }[];
  suspended?: boolean;
  suspended_reason?: string | null;
}

interface BoardPost {
  id: string;
  mine: boolean;
  game_name: string;
  mode: LfgMode;
  note: string | null;
  region: string | null;
  language: string | null;
  party_size: number;
  expires_at: string;
  author: { handle: string | null; display_name: string | null; links: Record<string, string> };
}

interface Collab {
  id: string;
  status: string;
  game_name: string;
  created_at: string;
  incoming: boolean;
  other: { handle: string | null; display_name: string | null; links: Record<string, string> };
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export async function profileView(root: HTMLElement): Promise<void> {
  const p = await api<Profile>("GET", "/v1/profile");

  const linkRows = LINK_PLATFORMS.map(
    (k) => `<label class="lf">${esc(LINK_LABELS[k])}
        <input data-link="${k}" value="${esc(p.links[k] ?? "")}"
               placeholder="${k === "discord" ? "username or invite" : k + ".com/you"}">
      </label>`,
  ).join("");

  root.innerHTML = `
    <h1>Your profile</h1>

    ${
      p.suspended
        ? `<div class="notice warn"><strong>This profile is hidden.</strong>
             ${esc(p.suspended_reason ?? "Reply to our email if you think that is wrong.")}</div>`
        : p.published_at
          ? `<div class="notice"><strong>Published.</strong> Anyone with the link can read this,
               and you appear on the players board. <button class="linkish" id="unpub">Make it
               private again</button></div>`
          : `<div class="notice"><strong>Private.</strong> Nothing here is visible to anybody
               else, and you cannot post on the players board yet. It stays that way until you
               press Publish.</div>`
    }

    <section class="card">
      <label class="lf">Handle
        <input id="handle" value="${esc(p.handle ?? "")}" placeholder="yourname"
               autocomplete="off" spellcheck="false">
      </label>
      <p class="hint" id="handleHint">Letters, numbers, hyphens and underscores. This is how
         people find you, and it cannot read as one of our own names.</p>

      <label class="lf">Display name
        <input id="dname" value="${esc(p.display_name ?? "")}" maxlength="50">
      </label>

      <label class="lf">About you
        <textarea id="bio" rows="3" maxlength="500">${esc(p.bio ?? "")}</textarea>
      </label>

      <label class="lf">Region
        <select id="region">
          <option value="">Not saying</option>
          ${REGIONS.map(
            (r) =>
              `<option value="${r}"${p.region === r ? " selected" : ""}>${esc(
                REGION_LABELS[r],
              )}</option>`,
          ).join("")}
        </select>
      </label>
      <p class="hint">A continent, not a city. Enough to know whether a game would be playable.</p>

      <label class="lf">Open to
        <select id="looking">
          <option value="none"${p.looking_for === "none" ? " selected" : ""}>Nothing right now</option>
          <option value="duo"${p.looking_for === "duo" ? " selected" : ""}>Playing with people</option>
          <option value="collab"${p.looking_for === "collab" ? " selected" : ""}>Streamer collabs</option>
          <option value="both"${p.looking_for === "both" ? " selected" : ""}>Both</option>
        </select>
      </label>

      <h2>Where to reach you</h2>
      <p class="hint">These are the only way anybody can answer your posts — Stream-Cut has no
         messages, on purpose. Only platforms you already use, and each link has to be on that
         platform's own site.</p>
      ${linkRows}

      ${
        p.main_games.length
          ? `<h2>Shown on your profile</h2>
             <p class="hint">Taken from clips you kept. Nothing else about your account is public.</p>
             <ul class="plain">${p.main_games
               .map((g) => `<li>${esc(g.game_name)} — ${g.clips} clip${g.clips === 1 ? "" : "s"}</li>`)
               .join("")}</ul>`
          : ""
      }

      <div class="row">
        <button class="btn" id="save">Save</button>
        ${p.published_at ? "" : `<button class="btn primary" id="publish">Save &amp; publish</button>`}
      </div>
      <p class="hint" id="saveResult"></p>
    </section>

    <section class="card">
      <h2>Blocked</h2>
      <p class="hint">They stop seeing you and you stop seeing them. They are never told.</p>
      <div id="blocks">Loading…</div>
      <label class="lf">Block a handle
        <input id="blockHandle" placeholder="handle" autocomplete="off">
      </label>
      <button class="btn small" id="blockBtn">Block</button>
      <p class="hint" id="blockResult"></p>
    </section>
  `;

  const el = <T extends HTMLElement>(id: string) => root.querySelector<T>("#" + id)!;
  const result = el("saveResult");

  // Validated as they type, with the same module the server uses, so the form
  // never accepts a handle the server will refuse.
  const handleInput = el<HTMLInputElement>("handle");
  handleInput.addEventListener("input", () => {
    const value = handleInput.value.trim();
    const problem = value ? handleProblem(value) : null;
    el("handleHint").textContent = problem
      ? handleProblemMessage(problem)
      : "Letters, numbers, hyphens and underscores. This is how people find you.";
    el("handleHint").className = problem ? "hint warn" : "hint";
  });

  const collect = () => {
    const links: Record<string, string> = {};
    root.querySelectorAll<HTMLInputElement>("[data-link]").forEach((i) => {
      if (i.value.trim()) links[i.dataset.link!] = i.value.trim();
    });
    return {
      handle: handleInput.value.trim() || undefined,
      display_name: el<HTMLInputElement>("dname").value.trim() || null,
      bio: el<HTMLTextAreaElement>("bio").value.trim() || null,
      region: (el<HTMLSelectElement>("region").value || null) as Region | null,
      looking_for: el<HTMLSelectElement>("looking").value,
      links,
    };
  };

  const save = async (publish: boolean) => {
    result.textContent = "Saving…";
    try {
      await api("PUT", "/v1/profile", { ...collect(), ...(publish ? { published: true } : {}) });
      result.textContent = publish ? "Published." : "Saved.";
      if (publish) await profileView(root);
    } catch (err) {
      result.textContent = err instanceof ApiError ? err.message : String(err);
      result.className = "hint warn";
    }
  };

  el("save").addEventListener("click", () => void save(false));
  root.querySelector("#publish")?.addEventListener("click", () => void save(true));
  root.querySelector("#unpub")?.addEventListener("click", () => {
    void (async () => {
      await api("PUT", "/v1/profile", { published: false });
      await profileView(root);
    })();
  });

  // --- blocks -------------------------------------------------------------

  const renderBlocks = async () => {
    const { blocks } = await api<{ blocks: { handle: string | null; created_at: string }[] }>(
      "GET",
      "/v1/blocks",
    );
    el("blocks").innerHTML = blocks.length
      ? `<ul class="plain">${blocks
          .map(
            (b) =>
              `<li>${esc(b.handle ?? "(deleted profile)")}
                 ${b.handle ? `<button class="linkish" data-unblock="${esc(b.handle)}">Unblock</button>` : ""}
               </li>`,
          )
          .join("")}</ul>`
      : `<p class="hint">Nobody.</p>`;
  };
  await renderBlocks();

  el("blockBtn").addEventListener("click", () => {
    void (async () => {
      const handle = el<HTMLInputElement>("blockHandle").value.trim();
      if (!handle) return;
      try {
        await api("POST", "/v1/blocks", { handle });
        el<HTMLInputElement>("blockHandle").value = "";
        el("blockResult").textContent = "Blocked.";
        await renderBlocks();
      } catch (err) {
        el("blockResult").textContent = err instanceof ApiError ? err.message : String(err);
      }
    })();
  });

  el("blocks").addEventListener("click", (e) => {
    const handle = (e.target as HTMLElement).closest<HTMLElement>("[data-unblock]")?.dataset
      .unblock;
    if (!handle) return;
    void (async () => {
      await api("DELETE", `/v1/blocks/${encodeURIComponent(handle)}`);
      await renderBlocks();
    })();
  });
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

export async function playersView(root: HTMLElement): Promise<void> {
  const [profile, board, collabs] = await Promise.all([
    api<Profile>("GET", "/v1/profile"),
    api<{ posts: BoardPost[] }>("GET", "/v1/lfg"),
    api<{ collabs: Collab[] }>("GET", "/v1/collabs"),
  ]);
  const now = Date.now();
  const published = profile.published_at !== null && !profile.suspended;

  const linkList = (links: Record<string, string>) => {
    const items = LINK_PLATFORMS.filter((k) => links[k]).map((k) => {
      const value = links[k]!;
      // A Discord username is not a URL, so it is shown as text. Rendering it
      // as a link would produce a dead one.
      return value.startsWith("https://")
        ? `<a href="${esc(value)}" target="_blank" rel="noopener noreferrer nofollow">${esc(
            LINK_LABELS[k],
          )}</a>`
        : `<span class="attr">${esc(LINK_LABELS[k])}: ${esc(value)}</span>`;
    });
    return items.length ? items.join(" · ") : `<span class="hint">No links on their profile</span>`;
  };

  root.innerHTML = `
    <h1>Players</h1>
    <p class="hint">Open posts from other streamers. Stream-Cut has no messages — you answer
       someone through the links on their profile, which are shown with every post.</p>

    ${
      published
        ? `<section class="card">
             <h2>Post</h2>
             <div class="row">
               <input id="pgame" placeholder="Game" maxlength="120">
               <select id="pmode">
                 ${LFG_MODES.map((m) => `<option value="${m}">${esc(LFG_MODE_LABELS[m])}</option>`).join("")}
               </select>
             </div>
             <input id="pnote" placeholder="Anything else (optional)" maxlength="${LFG_NOTE_MAX}">
             <div class="row">
               <select id="pregion">
                 <option value="">Any region</option>
                 ${REGIONS.map((r) => `<option value="${r}">${esc(REGION_LABELS[r])}</option>`).join("")}
               </select>
               <button class="btn primary" id="post">Post for 4 hours</button>
             </div>
             <p class="hint" id="postResult">Posts expire after four hours, because the question
                is who wants to play tonight.</p>
           </section>`
        : `<div class="notice"><strong>Publish your profile to post.</strong> A post is answered
             through the links on your profile, so an anonymous one has no way for anybody to
             reach you. <a href="#/profile">Set up your profile</a></div>`
    }

    ${
      collabs.collabs.length
        ? `<section class="card">
             <h2>Collaborations</h2>
             <table class="rx">
               <tbody>
                 ${collabs.collabs
                   .map(
                     (c) => `<tr data-collab="${c.id}">
                       <td><strong>${esc(c.other.display_name ?? c.other.handle ?? "Someone")}</strong>
                           <span class="attr">${esc(c.game_name)}</span></td>
                       <td>${linkList(c.other.links)}</td>
                       <td>${esc(c.status)}</td>
                       <td>${
                         c.status === "proposed" && c.incoming
                           ? `<button class="btn small primary" data-respond="accepted">Accept</button>
                              <button class="btn small" data-respond="declined">Decline</button>`
                           : c.status === "accepted"
                             ? `<button class="btn small" data-respond="done">Mark done</button>`
                             : ""
                       }</td>
                     </tr>`,
                   )
                   .join("")}
               </tbody>
             </table>
           </section>`
        : ""
    }

    <section class="card">
      <h2>Looking for players</h2>
      ${
        board.posts.length
          ? `<div class="posts">${board.posts
              .map(
                (p) => `<article class="post" data-post="${p.id}">
                  <header>
                    <strong>${esc(p.game_name)}</strong>
                    <span class="attr">${esc(LFG_MODE_LABELS[p.mode] ?? p.mode)}</span>
                    ${p.region ? `<span class="attr">${esc(REGION_LABELS[p.region as Region] ?? p.region)}</span>` : ""}
                    <span class="attr">${esc(expiresIn({ ...p, closed_at: null, hidden_at: null, user_id: "" }, now))}</span>
                  </header>
                  ${p.note ? `<p>${esc(p.note)}</p>` : ""}
                  <footer>
                    <span>${esc(p.author.display_name ?? p.author.handle ?? "Someone")}</span>
                    ${linkList(p.author.links)}
                    ${
                      p.mine
                        ? `<button class="linkish" data-close="${p.id}">Close</button>`
                        : `${p.mode === "collab" ? `<button class="btn small primary" data-answer="${p.id}">I'm in</button>` : ""}
                           <button class="linkish" data-report="${esc(p.author.handle ?? "")}">Report</button>`
                    }
                  </footer>
                </article>`,
              )
              .join("")}</div>`
          : `<p class="hint">Nothing open right now. Posts last four hours, so the board is
               quiet between evenings.</p>`
      }
      <p class="hint" id="boardResult"></p>
    </section>
  `;

  const reload = () => void playersView(root);

  root.querySelector("#post")?.addEventListener("click", () => {
    void (async () => {
      const game = root.querySelector<HTMLInputElement>("#pgame")!.value.trim();
      const out = root.querySelector("#postResult")!;
      if (!game) {
        out.textContent = "Say which game.";
        return;
      }
      try {
        await api("POST", "/v1/lfg", {
          game_name: game,
          mode: root.querySelector<HTMLSelectElement>("#pmode")!.value,
          note: root.querySelector<HTMLInputElement>("#pnote")!.value.trim() || null,
          region: root.querySelector<HTMLSelectElement>("#pregion")!.value || null,
        });
        reload();
      } catch (err) {
        out.textContent = err instanceof ApiError ? err.message : String(err);
      }
    })();
  });

  root.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const out = root.querySelector("#boardResult");

    const close = target.closest<HTMLElement>("[data-close]")?.dataset.close;
    if (close) {
      void api("DELETE", `/v1/lfg/${close}`).then(reload);
      return;
    }

    const answer = target.closest<HTMLElement>("[data-answer]")?.dataset.answer;
    if (answer) {
      void (async () => {
        try {
          await api("POST", `/v1/lfg/${answer}/collab`);
          reload();
        } catch (err) {
          if (out) out.textContent = err instanceof ApiError ? err.message : String(err);
        }
      })();
      return;
    }

    const respond = target.closest<HTMLElement>("[data-respond]")?.dataset.respond;
    if (respond) {
      const id = target.closest<HTMLElement>("[data-collab]")?.dataset.collab;
      if (id) void api("POST", `/v1/collabs/${id}/respond`, { status: respond }).then(reload);
      return;
    }

    const handle = target.closest<HTMLElement>("[data-report]")?.dataset.report;
    if (handle) void reportFlow(handle, out);
  });
}

/**
 * Reporting, as a prompt rather than a page.
 *
 * Deliberately two steps and no more. A report form with six fields is one
 * people abandon, and the thing that matters is that somebody said *this, for
 * that reason* — the detail is optional.
 */
async function reportFlow(handle: string, out: Element | null): Promise<void> {
  if (!handle) return;
  const reasons = REPORT_REASONS.map((r, i) => `${i + 1}. ${REPORT_REASON_LABELS[r]}`).join("\n");
  const picked = prompt(`Report ${handle}. Why?\n\n${reasons}\n\nType a number:`);
  if (!picked) return;
  const reason = REPORT_REASONS[Number(picked) - 1] as ReportReason | undefined;
  if (!reason) {
    if (out) out.textContent = "That was not one of the numbers.";
    return;
  }
  const detail = prompt("Anything we should know? (optional)") ?? "";
  try {
    await api("POST", "/v1/reports", { subject_handle: handle, reason, detail: detail || null });
    if (out) {
      out.textContent =
        "Reported. A person reads these — nothing is hidden automatically, because a count " +
        "of reports that hid things would make brigading a weapon.";
    }
  } catch (err) {
    if (out) out.textContent = err instanceof ApiError ? err.message : String(err);
  }
}

// ---------------------------------------------------------------------------
// Challenges
// ---------------------------------------------------------------------------

export async function challengesView(root: HTMLElement): Promise<void> {
  const { challenges } = await api<{ challenges: ChallengeState[] }>("GET", "/v1/challenges");

  root.innerHTML = `
    <h1>Challenges</h1>
    <p class="hint">Measured from your own clips, sessions and audience reactions — there is
       nothing to submit and nothing to tick off. Joining is opt-in: a challenge that quietly
       measured everybody would be surveillance with a badge on it.</p>

    ${
      challenges.length
        ? `<div class="posts">${challenges
            .map(
              (c) => `<article class="post${c.completedAt ? " done" : ""}" data-ch="${c.challenge.id}">
                <header>
                  <strong>${esc(c.challenge.title)}</strong>
                  <span class="attr">${esc(c.label)}</span>
                  ${c.active ? "" : `<span class="attr">finished</span>`}
                </header>
                <p>${esc(c.challenge.description)}</p>
                <div class="bar"><i style="width:${Math.round(c.share * 100)}%"></i></div>
                <footer>
                  ${
                    c.completedAt
                      ? `<span>Done — and it stays done, even if you delete the clips.</span>`
                      : c.joined
                        ? `<span>${c.progress} of ${c.challenge.target}</span>`
                        : c.active
                          ? `<button class="btn small primary" data-join="${c.challenge.id}">Join</button>`
                          : `<span class="hint">This one finished.</span>`
                  }
                </footer>
              </article>`,
            )
            .join("")}</div>`
        : `<p class="hint">No challenges running. They are weekly, so check back.</p>`
    }
  `;

  root.addEventListener("click", (e) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>("[data-join]")?.dataset.join;
    if (!id) return;
    void api("POST", `/v1/challenges/${id}/join`).then(() => challengesView(root));
  });
}
